import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { PlatformLogo } from '../components/PlatformLogo';
import { platforms } from '../mock/mock';
import { products as computedProducts } from '../lib/catalog';
import { PRICING_RULES, PANEL_REFERENCES, MARKET_DATA, computeMargin, applyPricing } from '../lib/pricing';
import { products as rawProducts } from '../mock/mock';
import {
  RefreshCw, Info, ShieldCheck, TrendingUp, Zap, Percent, Package,
  Search, Filter, Layers, ChevronRight, Link as LinkIcon, Lock, Sparkles, AlertTriangle
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const StatCard = ({ icon: Icon, label, value, hint, tint = '#ff8247' }) => (
  <div className="glass-card rounded-2xl p-5">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${tint}18` }}>
      <Icon className="w-5 h-5" style={{ color: tint }} />
    </div>
    <div className="text-white text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</div>
    <div className="text-white/50 text-xs mt-1">{label}</div>
    {hint && <div className="text-white/40 text-[10px] mt-2">{hint}</div>}
  </div>
);

const AdminPricing = () => {
  const [query, setQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [snapshot, setSnapshot] = useState(computedProducts);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(new Date());

  const rows = useMemo(() => {
    const out = [];
    for (const [platformKey, list] of Object.entries(snapshot)) {
      if (platformFilter !== 'all' && platformFilter !== platformKey) continue;
      const platform = platforms.find(p => p.id === platformKey);
      for (const prod of list) {
        for (const v of prod.variants) {
          const match = `${prod.name} ${platform?.name}`.toLowerCase().includes(query.toLowerCase());
          if (!match) continue;
          const hasMarket = v.market != null;
          const margin = computeMargin(v.price, v.supplier);
          out.push({ platform, platformKey, prod, variant: v, hasMarket, margin });
        }
      }
    }
    return out;
  }, [snapshot, query, platformFilter]);

  const stats = useMemo(() => {
    const all = rows;
    const withMarket = all.filter(r => r.hasMarket).length;
    const withoutMarket = all.length - withMarket;
    const totalMarginEUR = all.reduce((s, r) => s + r.margin.eur * 1, 0);
    const avgMarginPct = all.length ? all.reduce((s, r) => s + r.margin.pct, 0) / all.length : 0;
    return { total: all.length, withMarket, withoutMarket, avgMarginPct, totalMarginEUR };
  }, [rows]);

  const syncNow = () => {
    setSyncing(true);
    setTimeout(() => {
      // In a real setup we'd fetch supplier prices + market data via API.
      // Here we simply re-apply the pricing engine (idempotent for the demo).
      setSnapshot(applyPricing(rawProducts));
      setLastSync(new Date());
      setSyncing(false);
      toast({ title: 'Synchronisation terminée', description: 'Tous les prix ont été recalculés selon les règles.' });
    }, 900);
  };

  const platformOptions = [{ id: 'all', name: 'Toutes les catégories' }, ...platforms.map(p => ({ id: p.id, name: p.name }))];

  return (
    <div>
      <AnnouncementBar />
      <Header />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-14">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="px-2.5 py-1 rounded-full bg-[#ff8247]/10 border border-[#ff8247]/20 text-[#ff8247] text-[10px] font-bold uppercase tracking-widest">Admin</div>
              <span className="text-white/40 text-xs">Dernière sync : {lastSync.toLocaleString('fr-FR')}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-none">
              Moteur de <span className="gradient-text">tarification</span>
            </h1>
            <p className="text-white/60 mt-3 max-w-2xl">
              Analyse automatiquement les prix de vente selon les règles fournisseur + marché. Recalcul instantané à chaque synchronisation.
            </p>
          </div>
          <button onClick={syncNow} disabled={syncing}
            className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-70">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </button>
        </div>

        {/* Rules */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-[#ff8247]" />
              <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Règles de calcul</h3>
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#00b67a]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#00b67a] text-xs font-bold">1</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Jamais sous le prix fournisseur</div>
                  <div className="text-white/50 text-xs">Marge minimum de {((PRICING_RULES.MIN_MARGIN_OVER_SUPPLIER - 1) * 100).toFixed(0)}% appliquée en toutes circonstances</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#ff8247]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#ff8247] text-xs font-bold">2</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Si prix concurrents disponibles</div>
                  <div className="text-white/50 text-xs">
                    <code className="text-[#ff8247]">vente = max(marché × {PRICING_RULES.MARKET_UNDERCUT}, fournisseur × {PRICING_RULES.MIN_MARGIN_OVER_SUPPLIER})</code> — 5% sous la moyenne du marché
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#4285F4]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#4285F4] text-xs font-bold">3</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Sinon fallback ×{PRICING_RULES.FALLBACK_MULTIPLIER}</div>
                  <div className="text-white/50 text-xs">
                    <code className="text-[#4285F4]">vente = fournisseur × {PRICING_RULES.FALLBACK_MULTIPLIER}</code>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">4</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Recalcul automatique</div>
                  <div className="text-white/50 text-xs">À chaque sync et pour tout nouveau service importé — sans toucher aux catégories ni descriptions</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon className="w-5 h-5 text-[#ff8247]" />
              <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Panels concurrents</h3>
            </div>
            <div className="space-y-2">
              {PANEL_REFERENCES.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                      <Layers className="w-4 h-4 text-white/60" />
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">{p.name}</div>
                      <div className="text-white/40 text-[10px]">{p.apiUrl}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20">
                    <Lock className="w-3 h-3 text-yellow-400" />
                    <span className="text-yellow-400 text-[10px] font-bold uppercase tracking-widest">Clé API requise</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-white/70 text-xs leading-relaxed">
                Ces panels n'exposent pas de prix publics scrapables — leurs CGU l'interdisent. Fournis une clé API marchande pour activer la synchronisation en temps réel. En attendant, le fallback ×{PRICING_RULES.FALLBACK_MULTIPLIER} s'applique automatiquement.
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Package} label="Variantes tarifées" value={stats.total} hint="Toutes catégories confondues" />
          <StatCard icon={TrendingUp} label="Avec données marché" value={stats.withMarket} hint={`${stats.total ? Math.round(stats.withMarket / stats.total * 100) : 0}% du catalogue`} tint="#00b67a" />
          <StatCard icon={Zap} label="Fallback ×7 appliqué" value={stats.withoutMarket} hint="Données concurrents manquantes" tint="#4285F4" />
          <StatCard icon={Percent} label="Marge moyenne" value={`${stats.avgMarginPct.toFixed(0)}%`} hint="Sur le catalogue complet" tint="#ff2d6f" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher un service…"
              className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-2.5 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#ff8247] transition" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/60" />
            <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}
              className="bg-white/5 border border-white/10 text-white text-sm rounded-full px-4 py-2 focus:outline-none focus:border-[#ff8247]">
              {platformOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>

        {/* Pricing table */}
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-white/50 text-[10px] uppercase tracking-widest">
                  <th className="text-left py-3 px-4 font-semibold">Service</th>
                  <th className="text-right py-3 px-4 font-semibold">Qté</th>
                  <th className="text-right py-3 px-4 font-semibold">Fournisseur</th>
                  <th className="text-right py-3 px-4 font-semibold">Marché</th>
                  <th className="text-right py-3 px-4 font-semibold">Vente</th>
                  <th className="text-right py-3 px-4 font-semibold">Marge</th>
                  <th className="text-right py-3 px-4 font-semibold">Règle</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 100).map((r, i) => (
                  <tr key={`${r.prod.id}-${r.variant.qty}-${i}`} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${r.platform?.color}18` }}>
                          <PlatformLogo platform={r.platform?.id} className="w-4 h-4" color={r.platform?.id === 'instagram' ? undefined : r.platform?.color} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-white text-xs font-semibold truncate">{r.prod.name}</div>
                          <div className="text-white/40 text-[10px]">{r.platform?.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-white/80 text-xs font-semibold whitespace-nowrap">{r.variant.qty.toLocaleString('fr-FR')}</td>
                    <td className="text-right py-3 px-4 text-white/70 text-xs whitespace-nowrap">{r.variant.supplier.toFixed(4)} €</td>
                    <td className="text-right py-3 px-4 whitespace-nowrap">
                      {r.hasMarket
                        ? <span className="text-[#00b67a] text-xs font-semibold">{r.variant.market.toFixed(2)} €</span>
                        : <span className="text-white/30 text-xs">—</span>}
                    </td>
                    <td className="text-right py-3 px-4 text-white text-sm font-bold whitespace-nowrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{r.variant.price.toFixed(2)} €</td>
                    <td className="text-right py-3 px-4 whitespace-nowrap">
                      <span className={`text-xs font-semibold ${r.margin.pct >= 200 ? 'text-[#00b67a]' : r.margin.pct >= 50 ? 'text-[#ff8247]' : 'text-yellow-400'}`}>
                        +{r.margin.pct.toFixed(0)}%
                      </span>
                    </td>
                    <td className="text-right py-3 px-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${r.hasMarket ? 'bg-[#00b67a]/10 text-[#00b67a] border border-[#00b67a]/20' : 'bg-[#4285F4]/10 text-[#4285F4] border border-[#4285F4]/20'}`}>
                        {r.hasMarket ? 'Marché' : `×${PRICING_RULES.FALLBACK_MULTIPLIER}`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 100 && (
            <div className="p-4 border-t border-white/5 text-center text-white/50 text-xs">
              Affichage 100 / {rows.length} — utilisez le filtre pour affiner
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-8 p-4 glass-card rounded-2xl flex items-start gap-3">
          <Info className="w-5 h-5 text-[#ff8247] flex-shrink-0 mt-0.5" />
          <div className="text-white/70 text-xs leading-relaxed">
            <div className="text-white font-semibold text-sm mb-1">Comportement automatique</div>
            Chaque nouveau service importé depuis l'API fournisseur utilise <b>la même logique de calcul</b> — sans intervention manuelle. Les catégories, descriptions et paramètres restent intacts, seuls les prix sont mis à jour. La synchronisation est <b>idempotente</b> : lancer plusieurs fois donne le même résultat tant que les données d'entrée n'ont pas changé.
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="btn-ghost inline-flex items-center gap-2 text-sm">Retour au site <ChevronRight className="w-4 h-4" /></Link>
        </div>
      </div>

      <Footer showCTA={false} />
    </div>
  );
};

export default AdminPricing;
